import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

/** A page title row with an optional back link and actions (environment picker, primary action). */
export function PageHeader({
  title, subtitle, back, actions,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  back?: { to: string; label: string };
  actions?: ReactNode;
}) {
  return (
    <div className="page-head">
      <div style={{ minWidth: 0 }}>
        {back && (
          <Link to={back.to} className="t-label" style={{ color: 'var(--primary)' }}>
            ← {back.label}
          </Link>
        )}
        <h1 className="t-display" style={{ margin: back ? '8px 0 4px' : '0 0 4px' }}>{title}</h1>
        {subtitle && <div className="t-label muted">{subtitle}</div>}
      </div>
      {actions && <div className="page-head-actions">{actions}</div>}
    </div>
  );
}

export interface SubTab<K extends string> {
  key: K;
  label: string;
  /** Hidden when the admin's role cannot use it — a tab that can only 403 is worse than none. */
  hidden?: boolean;
}

/** In-page tabs (the `.subnav` style Leads already uses), as a real tablist. */
export function SubTabs<K extends string>({
  tabs, active, onChange, label,
}: {
  tabs: SubTab<K>[];
  active: K;
  onChange: (key: K) => void;
  label: string;
}) {
  return (
    <div className="subnav" role="tablist" aria-label={label}>
      {tabs.filter((t) => !t.hidden).map((tab) => (
        <button
          key={tab.key}
          type="button"
          role="tab"
          aria-selected={tab.key === active}
          className={`subnav-item${tab.key === active ? ' active' : ''}`}
          onClick={() => onChange(tab.key)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

/** A card with a heading row. */
export function Section({
  title, meta, actions, children, flush = false,
}: {
  title: ReactNode;
  meta?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  /** No body padding — for a table that runs edge to edge. */
  flush?: boolean;
}) {
  return (
    <section className="card">
      <div className="card-header">
        <h2 className="t-heading" style={{ margin: 0 }}>{title}</h2>
        <div className="card-header-side">
          {meta && <span className="t-label subtle">{meta}</span>}
          {actions}
        </div>
      </div>
      {flush ? children : <div className="card-body">{children}</div>}
    </section>
  );
}

/** Label / value pairs. */
export function KeyValues({ rows }: { rows: { label: ReactNode; value: ReactNode }[] }) {
  return (
    <div className="kv-list">
      {rows.map((row, index) => (
        <div className="kv-row" key={index}>
          <span className="t-label muted">{row.label}</span>
          <span className="t-body">{row.value}</span>
        </div>
      ))}
    </div>
  );
}

/** Keyset paging: "Load more" while the server returns a `next_cursor`. */
export function LoadMore({
  hasMore, loading, onLoad,
}: {
  hasMore: boolean;
  loading: boolean;
  onLoad: () => void;
}) {
  if (!hasMore) return null;
  return (
    <div className="paginator">
      <span className="t-label muted">More rows on the server</span>
      <button type="button" className="btn btn-ghost" onClick={onLoad} disabled={loading}>
        {loading ? 'Loading…' : 'Load more'}
      </button>
    </div>
  );
}

/** An em dash with the reason in its title — for "not collected", never a zero. */
export function Gap({ why }: { why: string }) {
  return (
    <span className="subtle" title={why} aria-label={why}>
      —
    </span>
  );
}
